/**
 * Client-side time windows (ZTA design evaluation F26, Phase 3).
 *
 * GET /access-logs and GET /sessions take no time parameters, so a page whose
 * header has a TimeRangePicker can only narrow the rows it has loaded. These
 * helpers do that one way everywhere, and say how far back the loaded rows
 * reach, so the page can tell the user whether the window is complete.
 *
 * GET /terminal-sessions-grouped does take start_date / end_date and is
 * filtered by the server; it does not use this file.
 */
import { formatRangeLong, type ResolvedRange, type TimeRange } from '@/lib/timeRange';

function toMs(v: string | null | undefined): number | null {
  if (!v) return null;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? null : t;
}

/** Whether a timestamp falls inside resolved bounds (an open bound always passes). */
export function inWindow(ts: string | null | undefined, w: ResolvedRange): boolean {
  const from = toMs(w.from);
  const to = toMs(w.to);
  if (from === null && to === null) return true;
  const t = toMs(ts);
  if (t === null) return false;
  if (from !== null && t < from) return false;
  if (to !== null && t > to) return false;
  return true;
}

export function filterToWindow<T>(rows: T[], ts: (row: T) => string | null | undefined, w: ResolvedRange): T[] {
  if (!w.from && !w.to) return rows;
  return rows.filter((r) => inWindow(ts(r), w));
}

/**
 * Whether more rows inside the window may exist beyond the loaded ones, for a
 * newest-first list: the server has more, and the oldest loaded row is not yet
 * older than the window's start.
 */
export function windowMayContinue<T>(rows: T[], ts: (row: T) => string | null | undefined, total: number, w: ResolvedRange): boolean {
  if (total <= rows.length) return false;
  const from = toMs(w.from);
  if (from === null) return true;
  let oldest = Infinity;
  for (const r of rows) {
    const t = toMs(ts(r));
    if (t !== null && t < oldest) oldest = t;
  }
  return oldest === Infinity || oldest >= from;
}

/** "last 24 hours", "Oct 1, 09:00 to now": the window, for running copy. */
export function describeWindow(range: TimeRange): string {
  const long = formatRangeLong(range);
  return range.kind === 'relative' ? long.charAt(0).toLowerCase() + long.slice(1) : long;
}

/** "in the last 24 hours", "from Oct 1, 09:00 to now", or "" for all time. */
export function windowPhrase(range: TimeRange): string {
  if (range.kind === 'all') return '';
  if (range.kind === 'relative') return `in the ${describeWindow(range)}`;
  return `from ${describeWindow(range)}`;
}
